import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import { resolveLandingPath } from './config/roleLanding.js';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import RequireRole from './components/RequireRole.jsx';
import RequireBusinessRole from './components/RequireBusinessRole.jsx';
import './App.scss';

// Pages and layouts are generated per the design reference — lazy-imported
// and wired here as each use case is built. Available page layouts (create-frontend-
// layout-components): _employee-web-credential-qr-page-layout, _ingredients-recipe-master-layout,
// _user-management-rbac-layout, auth-web-layout.
const LoginPage = lazy(() => import('./pages/login/index.jsx').then((m) => ({ default: m.LoginPage })));
const HomePage = lazy(() => import('./pages/home/index.jsx'));

// UC 70c551f3-e63f-4af9-bd58-36f9c41115c7 — "CAF-01: Set up tenant, cafeterias,
// users and role scoping" — rendered inside the shared _ingredients-recipe-master-layout.
const IngredientsRecipeMasterLayout = lazy(() => import('./layouts/_ingredients-recipe-master-layout.jsx'));
const TenantCafeteriaManagementPage = lazy(() => import('./pages/tenant-cafeteria-management/index.jsx'));

// UC 1ffb5918-693e-4b0f-9be7-f30d2d736784 — "CAF-02: Maintain ingredients, recipes and BOM lines
// with version effective dating" — rendered inside the shared _ingredients-recipe-master-layout.
const IngredientsRecipeMasterPage = lazy(() => import('./pages/ingredients-recipe-master/index.jsx'));

// UC 9a125829-3750-461b-a43c-eb8365b4f798 — "Manage system users, assign
// predefined roles, and configure role-based access control permissions" —
// rendered inside the shared _user-management-rbac-layout.
const UserManagementRbacLayout = lazy(() => import('./layouts/_user-management-rbac-layout.jsx'));
const UserManagementRbacPage = lazy(() => import('./pages/user-management-rbac/index.jsx'));

// UC 6cd6e154-ec70-417d-a93f-58f073dffbb4 — "CAF-03: Manage client companies,
// cafeteria assignment, billing terms, and service calendar" — rendered inside
// the shared _ingredients-recipe-master-layout (reused, not regenerated).
const ClientCompaniesContractsManagementPage = lazy(() => import('./pages/client-companies-contracts-management/index.jsx'));

// UC 7d541a69-675b-4633-a4f3-e37c6e0b447e — "CAF-04: Maintain company rate cards with effective
// dating" — rendered inside the shared _ingredients-recipe-master-layout (reused, not regenerated).
const RateCardsMealPacksPage = lazy(() => import('./pages/rate-cards-meal-packs/index.jsx'));

// UC 3ce06eb1-dfcf-4d8d-9b83-7a43e6b8ad32 — "CAF-06: Manage menu items and compose meal sets" —
// rendered inside the shared _ingredients-recipe-master-layout (reused, not regenerated).
const MenuItemsMealSetsPage = lazy(() => import('./pages/menu-items-meal-sets/index.jsx'));

// UC 7bec2567-38b9-4400-8232-d0b4f2cb01ad — "CAF-07: Plan rotating cycle menu per cafeteria,
// daypart, and day" — rendered inside the shared _ingredients-recipe-master-layout (reused, not
// regenerated).
const CycleMenuPlannerPage = lazy(() => import('./pages/cycle-menu-planner/index.jsx'));

// UC e7ad1b88-6c4f-4a28-a251-99307eb49815 — "CAF-08: Manage employee roster (CRUD + CSV bulk
// upload)" — rendered inside the shared _ingredients-recipe-master-layout (reused, not regenerated).
const EmployeeRosterManagementPage = lazy(() => import('./pages/employee-roster-management/index.jsx'));

// UC 93181d5c-3f5a-46c6-8f1a-51b458d38e7b — "CAF-09: Assign and remove employee meal benefits" —
// rendered inside the shared _ingredients-recipe-master-layout (reused, not regenerated).
const MealBenefitsAssignmentPage = lazy(() => import('./pages/meal-benefits-assignment/index.jsx'));

// UC e31c6601-b164-48af-a7da-deaf563a28fc — "CAF-31: Confirm upcoming employee attendance
// headcount and pre-order meal allocations per daypart for meal planning" — rendered inside the
// shared _ingredients-recipe-master-layout (reused, not regenerated).
const AttendancePreOrdersPage = lazy(() => import('./pages/attendance-pre-orders/index.jsx'));

// UC b8e49f1c-5a42-402d-8486-5b0dc31826d0 — "Display dynamic employee HMAC QR code with offline
// refresh capabilities for meal redemption" — rendered inside its own shared
// _employee-web-credential-qr-page-layout (chrome-light, matches the design's mobile-first card).
const EmployeeWebCredentialQrPageLayout = lazy(() => import('./layouts/_employee-web-credential-qr-page-layout.jsx'));
const EmployeeCredentialQrPage = lazy(() => import('./pages/employee-credential-qr/index.jsx'));

// UC e2a14ded-80b9-454b-8d85-07f5bfa77fcb — "Scan employee QR code, validate active status and
// meal entitlement cap, and log served meal set" (Counter Scan & Validation POS) — rendered
// inside the SAME shared _employee-web-credential-qr-page-layout (reused verbatim, not
// regenerated) — both are focused/kiosk-style single-screen POS/viewer pages.
const CounterScanPosPage = lazy(() => import('./pages/counter-scan-pos/index.jsx'));

// UC b58cba6c-b8f1-404e-9363-b958706e5541 — "Process meal redemptions during network outages
// using client-side offline queue and auto-sync" (Offline Queue & Sync Management) — the viewer +
// manual-control surface for the SAME local encrypted queue the Counter Scan POS above writes to;
// rendered inside the SAME shared _employee-web-credential-qr-page-layout.
const OfflineQueueSyncManagementPage = lazy(() => import('./pages/offline-queue-sync-management/index.jsx'));

// UC 286a38e6-68eb-4804-ac63-ea84ebd97ce4 — "Review and approve scan exception overrides with
// mandatory reason logging and manager credentials" (Manager Overrides) — rendered inside the
// shared _ingredients-recipe-master-layout (reused, not regenerated), reached from the Counter
// Scan POS's "Request Manager Override" control.
const ManagerOverridesPage = lazy(() => import('./pages/manager-overrides/index.jsx'));

// UC 5144901e-d1f9-4369-80fe-cfcb53d567a0 — "CAF-28: Configure KitchenOS connection, scopes, and
// entity mappings for cafeterias, menu items, and ingredients" — rendered inside the shared
// _ingredients-recipe-master-layout (reused, not regenerated).
const KitchenOsIntegrationSyncManagementPage = lazy(() => import('./pages/kitchenos-integration-sync-management/index.jsx'));

// UC 76f48d64-72a6-4fef-b0cd-096d14d839cf — "Log raw ingredient consumption, dish batch production
// logs, and cafeteria wastage tracking" — rendered inside the shared _ingredients-recipe-master-layout
// (reused, not regenerated).
const KitchenBatchProductionLogWastageTrackingPage = lazy(() =>
  import('./pages/kitchen-batch-production-log-wastage-tracking/index.jsx')
);

// UC 780542e3-e510-4d77-83ca-575f04ec2662 — "Reconcile actual scan redemptions against HR
// pre-order attendance and generate variance reports" — rendered inside the shared
// _ingredients-recipe-master-layout (reused, not regenerated).
const VarianceMealReconciliationWorkspacePage = lazy(() => import('./pages/variance-meal-reconciliation-workspace/index.jsx'));

// Task 5d (2026-08-02-supply-to-order-traceability) — "Shift Stock Count", the storekeeper
// capture screen behind the variance workspace's measured "actual drawn" figure. Not a synced
// RawHTMLDesign page (no PageDesignMap id) — rendered inside the same shared
// _ingredients-recipe-master-layout as its sibling Menu & Kitchen Operations pages.
const ShiftStockCountPage = lazy(() => import('./pages/shift-stock-count/index.jsx'));

// UC 3f3022a5-91dd-4e04-91aa-a95ff9b77671 — "Generate periodic consolidated client invoices with
// itemized meal breakdown and rate card pricing" — rendered inside the shared
// _ingredients-recipe-master-layout (reused, not regenerated).
const ClientInvoicesBillingManagementPage = lazy(() => import('./pages/client-invoices-billing-management/index.jsx'));

// UC 78ffc140-15ce-4412-a2bf-1e6452ac99c8 — "Record client invoice payments, manage partial
// payments, and track outstanding overdue balances" — the AR aggregate/portfolio view on top of
// UC 3f3022a5's invoices module — rendered inside the shared _ingredients-recipe-master-layout
// (reused, not regenerated).
const PaymentCollectionsOverdueArTrackingPage = lazy(() => import('./pages/payment-collections-overdue-ar-tracking/index.jsx'));

// UC 42af05e9-48fa-4ec1-90fc-c53fecfd7098 — "Enforce client company credit limits, trigger
// warning alerts, and apply service suspension locks" — rendered inside the shared
// _ingredients-recipe-master-layout (reused, not regenerated).
const CreditLimitRulesLockEnforcementPage = lazy(() => import('./pages/credit-limit-rules-lock-enforcement/index.jsx'));

// UC 1ce1dc5b-b28e-49ad-8b19-b5340068da18 — "Synchronize general ledger entries, sales receipts,
// and AR invoices to external accounting software" — rendered inside the shared
// _ingredients-recipe-master-layout (reused, not regenerated).
const AccountingErpLedgerSyncPage = lazy(() => import('./pages/accounting-erp-ledger-sync/index.jsx'));

// UC 23fe252c-5fc8-4ee2-800e-e7e856a5fe06 — "Generate company-specific meal consumption,
// entitlement utilization, and billing summary reports" (HR Consumption & Billing Statement
// Reports) — rendered inside the shared _ingredients-recipe-master-layout (reused, not
// regenerated).
const HrConsumptionBillingStatementReportsPage = lazy(() => import('./pages/hr-consumption-billing-statement-reports/index.jsx'));

// UC 007539f1-c51d-4b35-a861-635c2eedb5fd — "Generate system-wide operational reports covering
// multi-cafeteria volume, peak hours, and production trends" (Operator Analytics & System
// Dashboard) — rendered inside the shared _ingredients-recipe-master-layout (reused, not
// regenerated).
const OperatorAnalyticsSystemDashboardPage = lazy(() => import('./pages/operator-analytics-system-dashboard/index.jsx'));

// UC 0a7dbc98-a090-448b-bc62-ebcdc13d8669 — "CAF-31: Define service areas and coverage mapping"
// (Service Areas & Coverage Setup) — geographic service zones, industrial-park coverage, and
// postal-code coverage qualification for client onboarding. NOTE: distinct from the
// already-implemented CAF-31 "Attendance Pre-Orders & Headcount Confirmation" use case above (a
// catalog label collision, not a duplicate) — rendered inside the shared
// _ingredients-recipe-master-layout (reused, not regenerated).
const ServiceAreasCoverageSetupPage = lazy(() => import('./pages/service-areas-coverage-setup/index.jsx'));

// UC 757de86b-2900-4af6-aaab-b97f6e17817a — "CAF-32: Configure and publish operator's public
// landing page". Two surfaces: the Operator-Admin-only Landing Page Manager (config/publish,
// rendered inside the shared _ingredients-recipe-master-layout, reused, not regenerated) and the
// GENUINELY PUBLIC Operator Landing Page (registered below outside <ProtectedRoute />, same
// pattern as this app's own /login route — no session required to view it).
const LandingPageManagerPage = lazy(() => import('./pages/landing-page-manager/index.jsx'));
const OperatorLandingPage = lazy(() => import('./pages/operator-landing-page/index.jsx'));

// CAF-33 "Submit signup request for served area" (UC 46089101-a52e-4e26-a2db-21c4300790c4) — the
// Signup Request & Demand Intake Form, reached from the Operator Landing Page's "Get Started /
// Request Service" CTA. GENUINELY PUBLIC, same as OperatorLandingPage above — no session required.
const SignupRequestDemandIntakeFormPage = lazy(() => import('./pages/signup-request-demand-intake-form/index.jsx'));

// UC 54d6c7dd-8254-44cd-8184-cf989fa3a2d8 — "CAF-34: Review, qualify, and convert signup request
// into client company and invite HR Admin" (Lead Qualification & HR Invitation Workspace) — the
// Operator-Admin-only admin review queue for the inbound signup requests CAF-33's public form
// creates — rendered inside the shared _ingredients-recipe-master-layout (reused, not
// regenerated).
const LeadQualificationHrInvitationWorkspacePage = lazy(() =>
  import('./pages/lead-qualification-hr-invitation-workspace/index.jsx')
);

// Generic status pages (create-frontend-layout-components Policy 10) — themed fallbacks, not
// part of the raw design. Every menuData link whose real page isn't built yet points at /404.
const AuthWebLayout = lazy(() => import('./layouts/auth-web-layout.jsx'));
const UnauthorizedPage = lazy(() => import('./pages/status/UnauthorizedPage.jsx'));
const ForbiddenPage = lazy(() => import('./pages/status/ForbiddenPage.jsx'));
const NotFoundPage = lazy(() => import('./pages/status/NotFoundPage.jsx'));
const ServerErrorPage = lazy(() => import('./pages/status/ServerErrorPage.jsx'));

const Loader = () => (
  <div className="d-flex justify-content-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

/**
 * The site root, `/`, is the app's front door for two very different visitors, so it can't sit
 * behind <ProtectedRoute /> (which bounced every anonymous visitor — including a prospective
 * corporate HR lead arriving cold — straight to /login, leaving CAF-32's public Operator Landing
 * Page reachable only by typing its URL, and CAF-33's signup form only via that page's CTA).
 *
 * Anonymous  -> the public Operator Landing Page (same component as /operator-landing-page), whose
 *               "Submit Corporate Onboarding Lead" CTA is the entry to the signup request form.
 * Signed in  -> that user's business-role dashboard (frontend/src/config/roleLanding.js), matching
 *               where AuthForm sends them right after login.
 *
 * `resolveLandingPath` falls back to `/` for a user with no business role (or one whose dashboard
 * isn't built yet); redirecting there would loop, so that case renders the generic HomePage —
 * which is exactly what `/` served before.
 */
function RootEntry() {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) return <Loader />;
  if (!isAuthenticated) return <OperatorLandingPage />;

  const target = resolveLandingPath(user?.business_role, '/');
  return target === '/' ? <HomePage /> : <Navigate to={target} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route element={<ProtectedRoute />}>

              {/* Operator Admin only — the platform authz role, per backend requireRole('admin'). */}
              <Route element={<RequireRole role="admin" />}>
                <Route element={<IngredientsRecipeMasterLayout />}>
                  <Route path="/tenant-cafeteria-management" element={<TenantCafeteriaManagementPage />} />
                </Route>
                <Route element={<UserManagementRbacLayout />}>
                  <Route path="/user-management-rbac" element={<UserManagementRbacPage />} />
                </Route>
                <Route element={<IngredientsRecipeMasterLayout />}>
                  <Route
                    path="/kitchenos-integration-sync-management"
                    element={<KitchenOsIntegrationSyncManagementPage />}
                  />
                </Route>
                <Route element={<IngredientsRecipeMasterLayout />}>
                  <Route path="/operator-analytics-system-dashboard" element={<OperatorAnalyticsSystemDashboardPage />} />
                </Route>
                <Route element={<IngredientsRecipeMasterLayout />}>
                  <Route path="/service-areas-coverage-setup" element={<ServiceAreasCoverageSetupPage />} />
                </Route>
                <Route element={<IngredientsRecipeMasterLayout />}>
                  <Route path="/landing-page-manager" element={<LandingPageManagerPage />} />
                </Route>
                <Route element={<IngredientsRecipeMasterLayout />}>
                  <Route
                    path="/lead-qualification-hr-invitation-workspace"
                    element={<LeadQualificationHrInvitationWorkspacePage />}
                  />
                </Route>
              </Route>

              {/* Finance Admin (view + edit) — Operator Admin may also view, per
                  RequireBusinessRole's platform-admin bypass (matches the backend's
                  requireBusinessRole default); edit affordances are hidden/disabled
                  in-page for non-Finance-Admin viewers, and blocked server-side too. */}
              <Route element={<RequireBusinessRole roles={['Finance Admin']} />}>
                <Route element={<IngredientsRecipeMasterLayout />}>
                  <Route
                    path="/client-companies-contracts-management"
                    element={<ClientCompaniesContractsManagementPage />}
                  />
                </Route>
              </Route>

              {/* Finance Admin (view + edit) + Catering Manager (view) — Operator Admin may also
                  view, per RequireBusinessRole's platform-admin bypass. Edit affordances (create/
                  edit/clone a rate card version) are hidden in-page for non-Finance-Admin viewers,
                  and blocked server-side too (every mutating /api/rate-cards endpoint's
                  requireBusinessRole(['Finance Admin'], { allowPlatformAdmin: false })). */}
              <Route element={<RequireBusinessRole roles={['Finance Admin', 'Catering Manager']} />}>
                <Route element={<IngredientsRecipeMasterLayout />}>
                  <Route
                    path="/company-rate-cards-meal-packs-configuration"
                    element={<RateCardsMealPacksPage />}
                  />
                </Route>
              </Route>

              {/* Catering Manager (view + edit) + Cafeteria Manager (view) — Operator Admin may
                  also view, per RequireBusinessRole's platform-admin bypass. Edit affordances
                  (Save Menu Item / Save Meal Set / Duplicate) are hidden in-page for
                  non-Catering-Manager viewers, and blocked server-side too (every mutating
                  /api/menu-items and /api/meal-sets endpoint's
                  requireBusinessRole(['Catering Manager'], { allowPlatformAdmin: false })). */}
              <Route element={<RequireBusinessRole roles={['Catering Manager', 'Cafeteria Manager']} />}>
                <Route element={<IngredientsRecipeMasterLayout />}>
                  <Route path="/menu-items-meal-sets-composer" element={<MenuItemsMealSetsPage />} />
                  <Route path="/ingredients-recipe-master" element={<IngredientsRecipeMasterPage />} />
                </Route>
              </Route>

              {/* Catering Manager (view + edit) + Cafeteria Manager (view) + HR Admin (view) —
                  Operator Admin may also view, per RequireBusinessRole's platform-admin bypass.
                  Publish/assign/copy affordances are hidden in-page for non-Catering-Manager
                  viewers, and blocked server-side too (every mutating /api/cycle-menus endpoint's
                  requireBusinessRole(['Catering Manager'], { allowPlatformAdmin: false })). */}
              {/* Cafeteria Manager (view + edit — Log Batch Production / Record Wastage Entry) +
                  Catering Manager (view) — Operator Admin may also view, per RequireBusinessRole's
                  platform-admin bypass. Edit affordances are hidden in-page for non-Cafeteria-Manager
                  viewers, and blocked server-side too (every mutating /api/production-batches
                  endpoint's requireBusinessRole(['Cafeteria Manager'], { allowPlatformAdmin: false })). */}
              <Route element={<RequireBusinessRole roles={['Cafeteria Manager', 'Catering Manager']} />}>
                <Route element={<IngredientsRecipeMasterLayout />}>
                  <Route
                    path="/kitchen-batch-production-log-wastage-tracking"
                    element={<KitchenBatchProductionLogWastageTrackingPage />}
                  />
                </Route>
              </Route>

              {/* Cafeteria Manager (view + edit — EOD Sign-Off & Lock Reconciliation) + Finance
                  Admin (view) — Operator Admin may also view, per RequireBusinessRole's
                  platform-admin bypass. Sign-off affordances are hidden in-page for non-Cafeteria-
                  Manager viewers, and blocked server-side too (every mutating
                  /api/reconciliation endpoint's requireBusinessRole(['Cafeteria Manager'],
                  { allowPlatformAdmin: false })). */}
              <Route element={<RequireBusinessRole roles={['Cafeteria Manager', 'Finance Admin']} />}>
                <Route element={<IngredientsRecipeMasterLayout />}>
                  <Route path="/variance-meal-reconciliation-workspace" element={<VarianceMealReconciliationWorkspacePage />} />
                </Route>
              </Route>

              {/* Task 5d (2026-08-02-supply-to-order-traceability) — Cafeteria Manager only,
                  Operator Admin NOT exempted (allowPlatformAdmin={false}), matching every
                  /api/stock-counts endpoint's requireBusinessRole(['Cafeteria Manager'],
                  { allowPlatformAdmin: false }) — the physical shift count is that role's job,
                  not a report a platform admin needs to open. */}
              <Route element={<RequireBusinessRole roles={['Cafeteria Manager']} allowPlatformAdmin={false} />}>
                <Route element={<IngredientsRecipeMasterLayout />}>
                  <Route path="/shift-stock-count" element={<ShiftStockCountPage />} />
                </Route>
              </Route>

              <Route element={<RequireBusinessRole roles={['Catering Manager', 'Cafeteria Manager', 'HR Admin']} />}>
                <Route element={<IngredientsRecipeMasterLayout />}>
                  <Route path="/cycle-menu-planner" element={<CycleMenuPlannerPage />} />
                  {/* CAF-31 view: HR Admin + Cafeteria Manager + Catering Manager (edit is HR Admin
                      only — enforced in-page + server-side, matching the group above's shape). */}
                  <Route path="/attendance-pre-orders-headcount-confirmation" element={<AttendancePreOrdersPage />} />
                </Route>
              </Route>

              {/* HR Admin only (view + edit) — this page's permission spec is explicit ("HR Admin
                  only"), so allowPlatformAdmin is false: unlike the routes above, an Operator Admin
                  does NOT get an implicit view bypass here. */}
              <Route element={<RequireBusinessRole roles={['HR Admin']} allowPlatformAdmin={false} />}>
                <Route element={<IngredientsRecipeMasterLayout />}>
                  <Route path="/employee-roster-management" element={<EmployeeRosterManagementPage />} />
                  <Route path="/meal-benefits-assignment-entitlements" element={<MealBenefitsAssignmentPage />} />
                  {/* UC 23fe252c — HR Consumption & Billing Statement Reports (view + export). */}
                  <Route path="/hr-consumption-billing-statement-reports" element={<HrConsumptionBillingStatementReportsPage />} />
                </Route>
              </Route>

              {/* Employee + HR Admin (view/refresh only — no edit affordance exists on this page
                  at all), no Operator Admin bypass (this page's permission spec is explicit,
                  matching CAF-08/CAF-09's shape). Server-side: every /api/credentials endpoint's
                  requireBusinessRole(['Employee', 'HR Admin']). */}
              <Route element={<RequireBusinessRole roles={['Employee', 'HR Admin']} allowPlatformAdmin={false} />}>
                <Route element={<EmployeeWebCredentialQrPageLayout />}>
                  <Route path="/employee-web-credential-qr-page" element={<EmployeeCredentialQrPage />} />
                </Route>
              </Route>

              {/* Counter Operator (view + scan/serve) + Cafeteria Manager (view only — the
                  scan/serve controls are disabled in-page for them, and server-side
                  /api/meal-packs/:id/redemption-check's requireBusinessRole excludes Cafeteria
                  Manager entirely), no Operator Admin bypass (explicit permission spec, matching
                  the Employee QR page's shape). */}
              <Route element={<RequireBusinessRole roles={['Counter Operator', 'Cafeteria Manager']} allowPlatformAdmin={false} />}>
                <Route element={<EmployeeWebCredentialQrPageLayout />}>
                  <Route path="/counter-scan-validation-pos" element={<CounterScanPosPage />} />
                </Route>
              </Route>

              {/* Counter Operator + Cafeteria Manager — both view AND edit (design's explicit
                  permission spec), no Operator Admin bypass, matching the Counter Scan POS's own
                  shape immediately above. Server-side: POST /api/meal-packs/redemptions/batch-sync's
                  requireBusinessRole allows exactly these two roles. */}
              <Route element={<RequireBusinessRole roles={['Counter Operator', 'Cafeteria Manager']} allowPlatformAdmin={false} />}>
                <Route element={<EmployeeWebCredentialQrPageLayout />}>
                  <Route path="/offline-queue-sync-management" element={<OfflineQueueSyncManagementPage />} />
                </Route>
              </Route>

              {/* Cafeteria Manager (view + authorize overrides) + Counter Operator (view only — the
                  "Request Manager Override" control on Counter Scan POS navigates THEIR OWN
                  session here, so they must be able to reach the page even though they can't
                  submit) — Operator Admin may also view, per RequireBusinessRole's platform-admin
                  bypass (matches the backend's default requireBusinessRole(['Cafeteria Manager',
                  'Counter Operator']) on GET /api/scan-overrides). The "Authorize Override" submit
                  is disabled in-page for any non-Cafeteria-Manager viewer, and blocked server-side
                  too (POST /api/scan-overrides's requireBusinessRole(['Cafeteria Manager'],
                  { allowPlatformAdmin: false })). */}
              <Route element={<RequireBusinessRole roles={['Cafeteria Manager', 'Counter Operator']} />}>
                <Route element={<IngredientsRecipeMasterLayout />}>
                  <Route path="/manager-exception-overrides-void-audit" element={<ManagerOverridesPage />} />
                </Route>
              </Route>

              {/* Finance Admin (view + edit — Run Billing Cycle / Record Payment) + HR Admin
                  (read-only, scoped server-side to their own client company) — Operator Admin may
                  also view, per RequireBusinessRole's platform-admin bypass. Generate/record-payment
                  affordances are hidden in-page for non-Finance-Admin viewers, and blocked
                  server-side too (every mutating /api/invoices endpoint's
                  requireBusinessRole(['Finance Admin'], { allowPlatformAdmin: false })); HR Admin's
                  view is scoped server-side via resolveCompanyScope, never a client-side filter. */}
              <Route element={<RequireBusinessRole roles={['Finance Admin', 'HR Admin']} />}>
                <Route element={<IngredientsRecipeMasterLayout />}>
                  <Route path="/client-invoices-billing-management" element={<ClientInvoicesBillingManagementPage />} />
                </Route>
              </Route>

              {/* Finance Admin (view + edit — Record Payment / Send Overdue Reminder) + Operator
                  Admin (view only, per RequireBusinessRole's platform-admin bypass) — NOT HR Admin,
                  per this page's own permission spec (unlike the invoices directory above). Edit
                  affordances are hidden in-page for a non-Finance-Admin viewer, and blocked
                  server-side too (every mutating /api/invoices/ar-aging-adjacent endpoint's
                  requireBusinessRole(['Finance Admin'], { allowPlatformAdmin: false })). */}
              <Route element={<RequireBusinessRole roles={['Finance Admin', 'Operator Admin']} />}>
                <Route element={<IngredientsRecipeMasterLayout />}>
                  <Route path="/payment-collections-overdue-ar-tracking" element={<PaymentCollectionsOverdueArTrackingPage />} />
                </Route>
              </Route>

              {/* Finance Admin (view + edit — Save Credit Policy / Grant Override) + Operator Admin
                  (view only, per RequireBusinessRole's platform-admin bypass) — matches this page's
                  own permission spec. Edit affordances are hidden in-page for a non-Finance-Admin
                  viewer, and blocked server-side too (every mutating /api/credit-limits endpoint's
                  requireBusinessRole(['Finance Admin'], { allowPlatformAdmin: false })). */}
              <Route element={<RequireBusinessRole roles={['Finance Admin', 'Operator Admin']} />}>
                <Route element={<IngredientsRecipeMasterLayout />}>
                  <Route path="/credit-limit-rules-lock-enforcement" element={<CreditLimitRulesLockEnforcementPage />} />
                </Route>
              </Route>

              {/* Finance Admin (view + edit — Trigger ERP Synchronization / Retry Failed Records) +
                  Operator Admin (view only, per RequireBusinessRole's platform-admin bypass) —
                  matches this page's own permission spec. Edit affordances are hidden in-page for a
                  non-Finance-Admin viewer, and blocked server-side too (POST /api/accounting/sync and
                  POST /api/accounting/sync-runs/:runId/retry both use
                  requireBusinessRole(['Finance Admin'], { allowPlatformAdmin: false })). */}
              <Route element={<RequireBusinessRole roles={['Finance Admin', 'Operator Admin']} />}>
                <Route element={<IngredientsRecipeMasterLayout />}>
                  <Route path="/accounting-erp-ledger-sync" element={<AccountingErpLedgerSyncPage />} />
                </Route>
              </Route>
            </Route>

            {/* Public — CAF-32's Operator Landing Page, registered ahead of/outside the
                <ProtectedRoute /> group so anonymous visitors can reach it, same as /login below.
                Uses the shared _ingredients-recipe-master-layout per the design's own layout
                assignment (its AppNavbar already renders a Login affordance instead of account
                chrome when no session is active — see AppNavbar Policy 9), reused not
                regenerated. */}
            <Route element={<IngredientsRecipeMasterLayout />}>
              {/* Public front door — see RootEntry above. Shares this layout (and so the same
                  navbar, which renders a Login affordance when no session is active) with the
                  public landing page it renders for anonymous visitors. */}
              <Route path="/" element={<RootEntry />} />
              <Route path="/operator-landing-page" element={<OperatorLandingPage />} />
              {/* Public — CAF-33's Signup Request & Demand Intake Form (UC
                  46089101-a52e-4e26-a2db-21c4300790c4), reached from the landing page's "Get
                  Started / Request Service" CTA. Same public registration pattern as
                  /operator-landing-page above. */}
              <Route path="/signup-request-demand-intake-form" element={<SignupRequestDemandIntakeFormPage />} />
            </Route>

            {/* Chrome-free auth shell (RawHTMLDesign/layouts/auth-web-layout.html) — hosts the
                public login screen ahead of any guard, plus the generic status pages. */}
            <Route element={<AuthWebLayout />}>
              {/* Public — registered ahead of/outside the ProtectedRoute group so anonymous
                  users (and every redirect-to-login) can reach it. */}
              <Route path="/login" element={<LoginPage />} />

              <Route path="/401" element={<UnauthorizedPage />} />
              <Route path="/403" element={<ForbiddenPage />} />
              <Route path="/404" element={<NotFoundPage />} />
              <Route path="/500" element={<ServerErrorPage />} />
              {/* Catch-all — no route matched. */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
